import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CalendarCheck,
  ListTodo,
  Pause,
  Phone,
  PhoneIncoming,
  Play,
  RefreshCw,
  Siren,
} from 'lucide-react';
import { PageHeader, EmptyState, MetricCard } from '@/shared/ui';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './CallLogPage.module.css';
import { useCallLog } from '../hooks/useCallLog';
import { useSyncCalls } from '../hooks/useSyncCalls';
import { useCallAgents } from '../hooks/useCallAgents';
import { useCallStats } from '../hooks/useCallStats';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import { CallDetailPanel } from '../components/CallDetailPanel';
import {
  getCallbackNumber,
  getComplaint,
  getDisconnectionIssue,
  getDurationLabel,
  getOutcome,
  getPainLevel,
  getPatientName,
} from '../utils/callFields';
import type { InboundCall } from '../api/callsApi';
import { activatableProps } from '../utils/a11y';

type TimeFilterValue = '7d' | '14d' | '30d' | '90d' | 'all';

const TIME_FILTER_OPTIONS: { value: TimeFilterValue; label: string; days: number | null }[] = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '14d', label: 'Last 14 days', days: 14 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: 'all', label: 'All time', days: null },
];

const OUTCOME_PILL: Record<string, string> = {
  success: patterns.pillSuccess,
  error: patterns.pillError,
  neutral: patterns.pillMuted ?? '',
};

const QUEUE_PILL: Record<string, { label: string; className: string }> = {
  new: { label: 'Callback: new', className: patterns.pillWarning },
  in_progress: { label: 'Callback: in progress', className: patterns.pillInfo },
  handled: { label: 'Handled', className: patterns.pillSuccess },
  no_answer: { label: 'No answer', className: patterns.pillMuted },
};

function painLevelPillClass(painLevel: string): string {
  const n = parseInt(painLevel, 10);
  if (!Number.isNaN(n)) {
    if (n >= 7) return patterns.pillError;
    if (n >= 4) return patterns.pillWarning;
  }
  return patterns.pillMuted ?? '';
}

// Calendar days in the viewer's timezone — an 11pm call viewed at 9am the
// next morning is "Yesterday", not "Today" (a 24-hour difference said Today).
function calendarDaysAgo(d: Date, now: Date): number {
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((startOf(now) - startOf(d)) / 86_400_000);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = calendarDaysAgo(d, now);
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 0) return `Today ${timeStr}`;
  if (diffDays === 1) return `Yesterday ${timeStr}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${timeStr}`;
}

interface CallRowProps {
  call: InboundCall;
  isPlaying: boolean;
  onTogglePlay: (call: InboundCall) => void;
  onOpenDetail: (call: InboundCall) => void;
}

function CallRow({ call, isPlaying, onTogglePlay, onOpenDetail }: CallRowProps) {
  const patientName = getPatientName(call);
  const complaint = getComplaint(call);
  const painLevel = getPainLevel(call);
  const callbackNumber = getCallbackNumber(call);
  const queuePill = call.queueStatus ? QUEUE_PILL[call.queueStatus] : null;
  const outcome = getOutcome(call);
  const disconnectIssue = getDisconnectionIssue(call);
  const duration = getDurationLabel(call);
  const sourceLabel = call.source && call.source !== call.agentName ? call.source : null;

  return (
    <div
      className={`${styles.tableRow} ${styles.tableRowClickable}`}
      {...activatableProps(
        () => onOpenDetail(call),
        `Open call from ${patientName ?? call.callerNumber ?? 'unknown caller'}, ${formatDate(call.receivedAt)}`,
      )}
    >
      <span className={styles.stackCell}>
        <span className={styles.tableRowMuted}>{formatDate(call.receivedAt)}</span>
        {duration && <span className={styles.subText}>{duration}</span>}
      </span>
      <span className={styles.patientCell}>
        <span className={styles.patientName}>{patientName ?? 'Unknown'}</span>
        {call.priority === 'urgent' && <span className={patterns.pillError}>Urgent</span>}
        {call.priority === 'high' && <span className={patterns.pillWarning}>High</span>}
        {painLevel && <span className={painLevelPillClass(painLevel)}>Pain {painLevel}</span>}
      </span>
      {callbackNumber ? (
        <a
          href={`tel:${callbackNumber}`}
          className={styles.callbackLink}
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={12} aria-hidden />
          {callbackNumber}
        </a>
      ) : (
        <span className={styles.tableRowMuted}>—</span>
      )}
      <span className={styles.complaintCell} title={complaint ?? undefined}>
        {complaint ?? '—'}
      </span>
      <span className={styles.stackCell}>
        <span className={styles.complaintCell}>{call.agentName ?? 'Deleted agent'}</span>
        {sourceLabel && (
          <span className={styles.subText} title={sourceLabel}>
            {sourceLabel}
          </span>
        )}
      </span>
      <span className={styles.outcomeCell}>
        <span className={outcome ? (OUTCOME_PILL[outcome.kind] ?? '') : styles.tableRowMuted}>
          {outcome?.label ?? '—'}
        </span>
        {queuePill && (
          <span className={queuePill.className}>
            {call.handledOutcome
              ? `Handled — ${call.handledOutcome.replace('_', ' ')}`
              : queuePill.label}
          </span>
        )}
        {disconnectIssue && (
          <span className={styles.issueText} title="How the call ended on Retell">
            <AlertTriangle size={11} aria-hidden /> {disconnectIssue}
          </span>
        )}
        {call.notificationError && (
          <span className={patterns.pillError} title={call.notificationError}>
            Email failed
          </span>
        )}
      </span>
      <button
        type="button"
        className={styles.playButton}
        disabled={!call.recordingUrl}
        title={call.recordingUrl ? 'Play recording' : 'No recording available'}
        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePlay(call);
        }}
      >
        {isPlaying ? <Pause size={13} /> : <Play size={13} />}
      </button>
    </div>
  );
}

export function CallLogPage() {
  const [agentFilter, setAgentFilter] = useState<string>('');
  const [timeFilter, setTimeFilter] = useState<TimeFilterValue>('7d');
  // Callback-queue view: every open callback regardless of date.
  const [queueOnly, setQueueOnly] = useState(false);
  const rangeDays = queueOnly
    ? null
    : (TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.days ?? null);
  const {
    calls,
    total,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCallLog({
    agentId: agentFilter || undefined,
    days: rangeDays,
    openCallbacks: queueOnly,
  });
  const { data: stats } = useCallStats();
  const { agents } = useCallAgents();
  const { sync } = useSyncCalls();
  const canManage = useCanManageCalls();
  const toast = useToast();
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Track the open call by id so the panel re-renders from fresh query data
  // (e.g. after "Mark handled") instead of a stale snapshot.
  const selectedCall = calls.find((c) => c.id === selectedCallId) ?? null;
  const rangeLabel =
    TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.label.toLowerCase() ?? '';
  const isFiltered = !!agentFilter || rangeDays !== null || queueOnly;

  // Changing the filter can remove the playing row — stop the audio too, not
  // just the icon, or it plays on with no control left to stop it.
  useEffect(() => {
    audioRef.current?.pause();
    setPlayingId(null);
  }, [agentFilter, timeFilter, queueOnly]);

  function handleTogglePlay(call: InboundCall) {
    if (!call.recordingUrl) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (playingId === call.id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    audio.src = call.recordingUrl;
    setPlayingId(call.id);
    audio.play().catch(() => {
      setPlayingId(null);
      toast.error("Couldn't play this recording — it may no longer be available on Retell.");
    });
  }

  function handleSync() {
    sync.mutate(undefined, {
      onSuccess: (result) => {
        const added =
          result.synced === 0
            ? 'no new calls'
            : `${result.synced} new call${result.synced === 1 ? '' : 's'}`;
        if (result.failedAgents.length > 0) {
          const names = result.failedAgents.map((f) => f.agentName).join(', ');
          // Partial sync: say exactly which agents Retell didn't answer for.
          toast.warning(
            `Synced ${added}, but Retell couldn't be reached for: ${names}. Their calls may be missing — try again shortly.`,
          );
        } else if (result.synced === 0) {
          toast.success('Already up to date — no new calls found');
        } else {
          toast.success(`Synced ${added}`);
        }
      },
      onError: (err: Error) => toast.error(err.message || 'Sync failed'),
    });
  }

  return (
    <div className={patterns.page}>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className={styles.hiddenAudio} />

      <PageHeader
        title="Call Log"
        subtitle="Every inbound call, what the AI agent did, and which ad / page / mailer the number came from."
        actions={
          canManage ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw size={13} />}
              onClick={handleSync}
              loading={sync.isPending}
            >
              Sync from Retell
            </Button>
          ) : undefined
        }
      />

      {stats && (
        <div className={`${patterns.statGrid4} ${styles.statsRow}`}>
          <MetricCard
            title="Calls · last 7 days"
            value={stats.callsHandled}
            icon={PhoneIncoming}
            sub={`${stats.selfServed} handled by agent · ${stats.transferred} transferred`}
          />
          <MetricCard
            title="Open callbacks"
            value={stats.callbacksOpen}
            icon={ListTodo}
            tone={stats.callbacksOpen > 0 ? 'yellow' : 'default'}
            sub={`${stats.callbacksHandled} handled in the last 7 days`}
          />
          <MetricCard
            title="Urgent · last 7 days"
            value={stats.urgentFlags}
            icon={Siren}
            tone={stats.urgentFlags > 0 ? 'red' : 'default'}
          />
          <MetricCard
            title="Want to book · last 7 days"
            value={stats.bookedIntent}
            icon={CalendarCheck}
            tone="green"
            sub={
              stats.estimatedValue > 0
                ? `$${stats.estimatedValue.toLocaleString('en-US')} est. opportunity`
                : undefined
            }
          />
        </div>
      )}

      {stats && stats.notificationFailures > 0 && (
        <div className={styles.alertRow} role="alert">
          <AlertTriangle size={14} aria-hidden />
          {stats.notificationFailures} call{stats.notificationFailures === 1 ? '' : 's'} in the last
          7 days couldn't be emailed to the front desk. They're marked “Email failed” below — open
          each one and follow up directly.
        </div>
      )}

      <div className={styles.filterBar}>
        <button
          type="button"
          className={`${styles.queueToggle} ${queueOnly ? styles.queueToggleActive : ''}`}
          aria-pressed={queueOnly}
          onClick={() => setQueueOnly((v) => !v)}
        >
          <ListTodo size={13} aria-hidden /> Open callbacks
          {stats ? ` (${stats.callbacksOpen})` : ''}
        </button>
        {agents.length > 0 && (
          <select
            className={styles.agentSelect}
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            aria-label="Filter by agent"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}
        <select
          className={styles.agentSelect}
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value as TimeFilterValue)}
          aria-label="Filter by date"
          disabled={queueOnly}
          title={queueOnly ? 'Open callbacks are shown regardless of date' : undefined}
        >
          {TIME_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className={styles.callCount}>
          {isLoading ? 'Loading…' : `${total} call${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {isError ? (
        <EmptyState
          icon={<AlertTriangle size={22} />}
          title="Couldn't load calls"
          description="The call log didn't load. Your calls are safe — try again in a moment."
          actions={
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ) : !isLoading && calls.length === 0 && !isFiltered ? (
        <EmptyState
          icon={<PhoneIncoming size={22} />}
          title="No calls yet"
          description="Once your agent is live, every handled call will appear here. Missing one? Use Sync from Retell."
        />
      ) : (
        <div className={`${patterns.panel} ${styles.table}`}>
          <div className={styles.tableHeader}>
            <span>When</span>
            <span>Patient</span>
            <span>Callback</span>
            <span>Reason</span>
            <span>Agent · source</span>
            <span>Outcome</span>
            <span />
          </div>
          {!isLoading && calls.length === 0 && (
            <div className={styles.emptyRow}>
              {queueOnly
                ? 'No open callbacks — the queue is clear. '
                : `No calls ${agentFilter ? 'for this agent ' : ''}${
                    rangeDays !== null ? `in the ${rangeLabel}` : 'yet'
                  }. `}
              {!queueOnly && rangeDays !== null && rangeDays < 90 && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setTimeFilter('all')}
                >
                  Show all time
                </button>
              )}
            </div>
          )}
          {calls.map((c) => (
            <CallRow
              key={c.id}
              call={c}
              isPlaying={playingId === c.id}
              onTogglePlay={handleTogglePlay}
              onOpenDetail={(call) => setSelectedCallId(call.id)}
            />
          ))}
          {hasNextPage && (
            <div className={styles.loadMore}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      )}

      <p className={styles.footnote}>
        Lead-level attribution. No PMS write-back; no revenue attribution.
      </p>

      <CallDetailPanel call={selectedCall} onClose={() => setSelectedCallId(null)} />
    </div>
  );
}
